import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useProjects } from "@/hooks/use-projects";
import { UserMenu } from "@/components/projects/user-menu/user-menu";
import { useRecentProjects } from "@/hooks/use-recent-projects";
import { HeroCard } from "@/components/projects/hero-card/hero-card";
import { ProjectList } from "@/components/projects/project-list/project-list";
import { EmptyProjectsState } from "@/components/projects/empty-projects-state/empty-projects-state";
import { AddProjectDialog } from "@/components/projects/add-project-dialog/add-project-dialog";
import { InstallAppBanner } from "@/components/common/install-app-banner/install-app-banner";
import { OtusLoader } from "@/components/common/otus-loader/otus-loader";
import { useOnlineStatus } from "@/offline/online-status";
import { isPinnedSync, usePinnedProjects } from "@/offline/pins";
import { canViewProjectPhotos } from "@/lib/project-tabs";
import type { Project } from "@/types/api";

export const Route = createFileRoute("/")({
  component: ProjectsPage,
});

function pickMostRecent(projects: Project[]): Project | null {
  if (projects.length === 0) return null;
  const sorted = [...projects]
    .filter((p) => p.last_photo_at)
    .sort(
      (a, b) =>
        new Date(b.last_photo_at!).getTime() -
        new Date(a.last_photo_at!).getTime(),
    );
  return sorted[0] ?? projects[0];
}

function ProjectsPage() {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const { data: profile } = useCurrentUser();
  const { lastAccessedId } = useRecentProjects();
  const isOnline = useOnlineStatus();
  const { data: pinnedRows } = usePinnedProjects();

  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
  } = useProjects();

  const projects = data?.projects;
  const totalCount = data?.totalCount ?? 0;

  // Determine hero project, from the projects whose photos the user can see —
  // the card is built around photos, so a project they only contribute to is
  // never it ([[hero-card.spec]]):
  // 1. last-accessed (from localStorage) if it's in the visible list
  // 2. fallback to most-recent-by-photo-timestamp
  // 3. when offline, if the chosen hero isn't pinned, swap to the most-recent
  //    pinned project. If nothing is pinned offline, hide the hero card.
  //    The eyebrow drops "Last Accessed" after a swap since the new hero
  //    isn't the row the user last touched.
  const profileId = profile?.id;
  const { hero: heroProject, isLastAccessed: heroIsLastAccessed } = useMemo(() => {
    const candidates = (projects ?? []).filter((p) =>
      canViewProjectPhotos(p, profileId),
    );
    if (candidates.length === 0) {
      return { hero: null as Project | null, isLastAccessed: false };
    }

    const lastAccessed = lastAccessedId
      ? candidates.find((p) => p.id === lastAccessedId)
      : undefined;
    const initial = lastAccessed ?? pickMostRecent(candidates);
    const initialIsLastAccessed = !!lastAccessed;

    if (isOnline) {
      return { hero: initial, isLastAccessed: initialIsLastAccessed };
    }

    // Offline path: rely on the synchronous, boot-populated pin cache so the
    // hero card doesn't pop in late once the IDB query resolves. The status
    // check (`row.status !== "error"`) only narrows the eligibility further;
    // for the offline-eligibility default, sync truth is enough.
    const erroredPins = new Set(
      (pinnedRows ?? [])
        .filter((row) => row.status === "error")
        .map((row) => row.projectId),
    );
    const eligibleForOffline = (p: Project) =>
      isPinnedSync(p.id) && !erroredPins.has(p.id);

    if (initial && eligibleForOffline(initial)) {
      return { hero: initial, isLastAccessed: initialIsLastAccessed };
    }

    const fallback = pickMostRecent(candidates.filter(eligibleForOffline));
    return { hero: fallback, isLastAccessed: false };
  }, [projects, profileId, lastAccessedId, isOnline, pinnedRows]);

  // Hold the same loader the AuthGuard just showed, so the handoff from sign-in
  // to the first screen has no blank frame. See [[otus-loader.spec]].
  if (isLoading) {
    return <OtusLoader fullScreen />;
  }

  const hasProjects = totalCount > 0;

  return (
    <main className="mx-auto flex max-w-4xl flex-col gap-4 px-4 py-8 h-app-screen">
      {/* Header */}
      <header
        className="sticky top-0 z-40 flex flex-col gap-1 bg-background pb-2"
        style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 1rem)" }}
      >
        <div className="flex items-center justify-between h-[50px]">
          {/* The wordmark is dark ink, so dark mode swaps in a copy with a light
              wordmark (same mint ring and blue arrow). Class-driven, because the
              `dark` class is the theme, not the device setting. See [[theming]]. */}
          <img
            src="/otus-logo.svg"
            alt="OTUS"
            width={151}
            height={50}
            className="h-full w-auto object-contain shrink-0 dark:hidden"
          />
          <img
            src="/otus-logo-dark.svg"
            alt="OTUS"
            width={151}
            height={50}
            className="hidden h-full w-auto object-contain shrink-0 dark:block"
          />
        </div>
        <div className="flex items-center justify-between min-h-10">
          <h1 className="text-xl font-semibold text-foreground">
            Projects
          </h1>
          <div className="flex items-center gap-2">
            {hasProjects && isOnline && (
              <Button
                className="cursor-pointer rounded-full"
                onClick={() => setAddDialogOpen(true)}
              >
                New Project
              </Button>
            )}
            {profile && <UserMenu user={profile} />}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1">
        {!hasProjects ? (
          <EmptyProjectsState
            isOffline={!isOnline}
            action={
              isOnline ? (
                <Button
                  className="cursor-pointer rounded-full"
                  onClick={() => setAddDialogOpen(true)}
                >
                  New Project
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="space-y-6">
            {heroProject && <HeroCard project={heroProject} isLastAccessed={heroIsLastAccessed} />}
            <ProjectList
              projects={projects ?? []}
              hasNextPage={!!hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              fetchNextPage={fetchNextPage}
              heroProjectId={heroProject?.id}
            />
          </div>
        )}
      </div>

      {/* Version label (non-production only) */}
      {import.meta.env.DEV && (
        <footer className="text-center py-2 text-xs text-muted-foreground">
          dev
        </footer>
      )}

      {/* Add Project Dialog */}
      <AddProjectDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
      />

      {/* No bottom strip and no FABs here. See [[install-app-banner.spec]]. */}
      <InstallAppBanner />
    </main>
  );
}
